import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined'
import type { InputBaseProps, Theme } from '@mui/material'
import { InputBase, ListSubheader } from '@mui/material'
import React from 'react'
import type { MrtColumnConfig } from '../mrtCollectionTypes'
import { MrtColumnGroup, MrtColumnSubgroup } from '../mrtCollectionTypes'
import type { FilterCrumb } from './FilterBreadcrumbs'

export const GROUP_LABEL_IDS: Record<MrtColumnGroup, string> = {
  [MrtColumnGroup.Provider]: 'filters.groups.provider',
  [MrtColumnGroup.Product]: 'filters.groups.product',
  [MrtColumnGroup.Risk]: 'filters.groups.risk',
  [MrtColumnGroup.Assessment]: 'filters.groups.assessment',
}

export const getGroupCrumbs = <T,>(
  column: MrtColumnConfig<T>,
  groupLabel: (group: MrtColumnGroup) => string,
  onBack?: () => void,
): FilterCrumb[] => (column.group ? [{ label: groupLabel(column.group), onClick: onBack }] : [])

export const SUBGROUP_LABEL_IDS: Record<MrtColumnSubgroup, string> = {
  [MrtColumnSubgroup.Page]: 'filters.subgroups.page',
  [MrtColumnSubgroup.Identity]: 'filters.subgroups.identity',
  [MrtColumnSubgroup.CompanyDetails]: 'filters.subgroups.companyDetails',
  [MrtColumnSubgroup.ContactDetails]: 'filters.subgroups.contactDetails',
  [MrtColumnSubgroup.StatusAndAdministration]: 'filters.subgroups.statusAndAdministration',
  [MrtColumnSubgroup.StandardCategories]: 'filters.subgroups.standardCategories',
  [MrtColumnSubgroup.CustomCategories]: 'filters.subgroups.customCategories',
  [MrtColumnSubgroup.SupplyChain]: 'schemas.product.supplyChain',
  [MrtColumnSubgroup.Status]: 'general.status',
}

export const GROUP_ICONS: Record<MrtColumnGroup, React.ReactElement> = {
  [MrtColumnGroup.Provider]: <BusinessOutlinedIcon fontSize="small" />,
  [MrtColumnGroup.Product]: <AccountTreeOutlinedIcon fontSize="small" />,
  [MrtColumnGroup.Risk]: <SpeedOutlinedIcon fontSize="small" />,
  [MrtColumnGroup.Assessment]: <AssessmentOutlined fontSize="small" />,
}

// The theme disables ripples, which are MUI's only focus indicator on ButtonBase controls, so keyboard
// focus would otherwise be invisible. Inset so scrolling lists don't clip it.
export const focusRing = (theme: Theme) => ({
  outline: `2px solid ${theme.palette.text.primary}`,
  outlineOffset: -2,
})

/**
 * The grouped filter dropdown's plain grey search field. A plain InputBase: the shared TextField's filled
 * variant brings a label slot and an underline the design doesn't have.
 */
export const MenuSearchInput: React.FC<
  { placeholder: string } & Pick<InputBaseProps, 'value' | 'onChange' | 'autoFocus'>
> = ({ placeholder, ...rest }) => (
  <InputBase
    fullWidth
    placeholder={placeholder}
    inputProps={{ 'aria-label': placeholder }}
    sx={{ typography: 'body1', bgcolor: 'grey.100', borderRadius: 2, px: 1.25, py: 0.5 }}
    {...rest}
  />
)

// The theme fixes menu items at a single-line height; these rows wrap and are spaced to the design.
export const menuOptionSx = {
  justifyContent: 'space-between',
  gap: 1,
  height: 'auto',
  minHeight: 0,
  px: 1.25,
  py: 1,
  borderRadius: 2,
  whiteSpace: 'normal',
}

/** A heading above a group of options in a filter dropdown's list. */
export const MenuGroupHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ListSubheader
    disableSticky
    sx={theme => ({
      ...theme.typography.caption,
      color: 'text.disabled',
      lineHeight: 'inherit',
      px: 1.25,
      pt: 1.25,
      pb: 0.5,
      '&:not(:first-of-type)': { mt: 1 },
    })}
  >
    {children}
  </ListSubheader>
)
